import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import CancelAlertDialog from "@/src/components/molecule/CancelAlertDialog";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import FormItem from "@/src/components/molecule/FormItem";
import { emailRegex } from "@/src/utils/regex";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

type ContactPersonAddModalFormType = {
  name: string;
  email: string;
  personalContactPrefix: string;
  personalContact: string;
  officeContactPrefix: string;
  officeContact: string;
  isActivated: string;
};

export type TransformContactPersonAddModalFormType = Omit<
  ContactPersonAddModalFormType,
  "isActivated"
> & { isActivated: boolean };

interface ContactPersonAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCountryCode?: string;
  onSave: (values: TransformContactPersonAddModalFormType) => void;
}

const ContactPersonAddDialog = ({
  open,
  onOpenChange,
  defaultCountryCode,
  onSave,
}: ContactPersonAddDialogProps) => {
  const { t } = useTranslation();
  const dialogSubmitButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);

  const {
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<ContactPersonAddModalFormType>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      personalContactPrefix: defaultCountryCode,
      personalContact: "",
      officeContactPrefix: defaultCountryCode,
      officeContact: "",
      isActivated: "Y",
    },
  });

  useEffect(() => {
    setFocus("name");
  }, [setFocus]);

  const renderAlertDialog = () => {
    if (isCancelAlertOpen) {
      return (
        <CancelAlertDialog
          open={isCancelAlertOpen}
          onOpenChange={setIsCancelAlertOpen}
          onOk={() => onOpenChange(false)}
        />
      );
    }
  };

  return (
    <Dialog
      title={t("warehouseManagement:factoryListAddPage.addContactPerson")}
      onOpenChange={onOpenChange}
      open={open}
      destroyDialogWhenEscapePress={false}
      onEscapeKeyDown={() => {
        setIsCancelAlertOpen(true);
      }}
      footer={
        <DialogFooterContainer>
          <Button
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={() => setIsCancelAlertOpen(true)}
          >
            {t("warehouseManagement:common.exit")}
          </Button>
          <Button onClick={() => dialogSubmitButtonRef.current?.click()}>
            {t("warehouseManagement:common.save")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <DialogForm
        onSubmit={handleSubmit((values) =>
          onSave({
            ...values,
            personalContact: values.personalContact.replace(/^0+/, ""), // 앞자리 0 제거
            officeContact: values.officeContact.replace(/^0+/, ""), // 앞자리 0 제거
            isActivated: values.isActivated === "Y",
          }),
        )}
      >
        <FormItem
          type="text"
          direction="vertical"
          label={t("warehouseManagement:common.name")}
          control={control}
          name="name"
          rules={{ required: true }}
          inputProps={{
            placeholder: t("warehouseManagement:placeholder.name"),
          }}
          errorsMessage={{
            required: t("error:required"),
          }}
        />
        <FormItem
          type="text"
          direction="vertical"
          label={t("warehouseManagement:common.email")}
          control={control}
          name="email"
          rules={{ required: true, pattern: emailRegex }}
          inputProps={{
            placeholder: t("warehouseManagement:placeholder.email"),
          }}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("warehouseManagement:common.personalContact")}
          direction="vertical"
          prefixName="personalContactPrefix"
          restContactName="personalContact"
          prefixValue={watch("personalContactPrefix") || undefined}
          errors={errors}
          isRequired
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("warehouseManagement:common.officeContact")}
          direction="vertical"
          prefixName="officeContactPrefix"
          restContactName="officeContact"
          prefixValue={watch("officeContactPrefix") || undefined}
          errors={errors}
          isRequired={false}
        />
        <FormItem
          type="radioGroup"
          direction="vertical"
          label={t("warehouseManagement:common.use")}
          control={control}
          name="isActivated"
          rules={{ required: true }}
          options={[
            { label: "Y", value: "Y" },
            { label: "N", value: "N" },
          ]}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />
        {renderAlertDialog()}
        <Hidden type="submit" ref={dialogSubmitButtonRef} />
      </DialogForm>
    </Dialog>
  );
};

export default ContactPersonAddDialog;

const DialogForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;
